import React, { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { Info, Save, RotateCw, Wallet } from "lucide-react";

const FIELDS = [
  { key: "basic_da", label: "BASIC+DA", suffix: "\u20B9", step: "1" },
  { key: "hra", label: "HRA", suffix: "\u20B9", step: "1" },
  { key: "other_allowances", label: "Other Allowances", suffix: "\u20B9", step: "1" },
  { key: "pf_percent", label: "Employee PF %", suffix: "%", step: "0.01" },
  { key: "employer_pf_percent", label: "Employer PF %", suffix: "%", step: "0.01" },
  { key: "pf_wage_cap", label: "PF Wage Cap", suffix: "\u20B9", step: "1" },
  { key: "esi_percent", label: "Employee ESI %", suffix: "%", step: "0.01" },
  { key: "employer_esi_percent", label: "Employer ESI %", suffix: "%", step: "0.01" },
  { key: "pt_amount", label: "PT (Monthly)", suffix: "\u20B9", step: "1" },
];

export default function PayrollRatesPage() {
  const [rates, setRates] = useState([]);
  const [edits, setEdits] = useState({}); // {designation: {field: value}}
  const [savingFor, setSavingFor] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/payroll-rates").then((x) => x.data);
      setRates(r);
      setEdits({});
    } catch {
      toast.error("Failed to load payroll rates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setVal = (designation, key, value) => {
    setEdits((cur) => ({
      ...cur,
      [designation]: { ...(cur[designation] || {}), [key]: value },
    }));
  };

  const merged = useMemo(() => {
    return rates.map((r) => ({ ...r, ...(edits[r.designation] || {}) }));
  }, [rates, edits]);

  const dirty = (designation) => !!edits[designation] && Object.keys(edits[designation]).length > 0;

  const save = async (designation) => {
    const patch = edits[designation] || {};
    // Coerce to numbers
    const payload = {};
    for (const [k, v] of Object.entries(patch)) {
      const num = Number(v);
      if (Number.isFinite(num)) payload[k] = num;
    }
    if (Object.keys(payload).length === 0) return;
    setSavingFor(designation);
    try {
      await api.put(`/admin/payroll-rates/${designation}`, payload);
      toast.success(`${designation} rates updated`);
      await load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSavingFor(null);
    }
  };

  return (
    <AppShell
      title="Payroll Rates"
      subtitle="Global salary components per designation (used by the Salary Report)"
    >
      <div className="space-y-5">
        <Alert className="border-amber-200 bg-amber-50/60" data-testid="pr-info-alert">
          <Info className="h-4 w-4 text-amber-700" />
          <AlertTitle className="text-amber-900">How payroll rates work</AlertTitle>
          <AlertDescription className="text-amber-900/90 text-[13px] leading-relaxed">
            These rates are applied <strong>globally</strong> across every park when the monthly
            Salary workbook is generated. Earned components are pro-rated by
            <em> Days Working &divide; Total Days in Month</em>. Statutory deductions follow standard
            formulas:
            <span className="block mt-1">
              <strong>PF</strong> = min(BASIC+DA earned, PF Cap) &times; PF% &nbsp;|&nbsp;
              <strong>ESI</strong> = Gross earned &times; ESI%
            </span>
          </AlertDescription>
        </Alert>

        {loading ? (
          <div className="text-sm text-muted-foreground">Loading&hellip;</div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {merged.map((r) => (
              <Card key={r.designation} data-testid={`pr-card-${r.designation}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center justify-between text-[15px]">
                    <span className="flex items-center gap-2">
                      <Wallet className="h-4 w-4 text-amber-700" /> {r.designation}
                    </span>
                    {dirty(r.designation) && (
                      <Badge variant="outline" className="border-amber-400 bg-amber-50 text-amber-700">unsaved</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {FIELDS.map((f) => (
                    <div key={f.key} className="flex items-center gap-2">
                      <Label className="w-32 shrink-0 text-[12px] text-slate-600">{f.label}</Label>
                      <div className="relative flex-1">
                        <Input
                          type="number"
                          step={f.step}
                          inputMode="decimal"
                          value={r[f.key] ?? 0}
                          onChange={(e) => setVal(r.designation, f.key, e.target.value)}
                          className="pr-7 text-right tabular-nums"
                          data-testid={`pr-${r.designation}-${f.key}`}
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-slate-500">{f.suffix}</span>
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center justify-end gap-2 pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEdits((cur) => { const n = { ...cur }; delete n[r.designation]; return n; })}
                      disabled={!dirty(r.designation) || savingFor === r.designation}
                      data-testid={`pr-reset-${r.designation}`}
                    >
                      <RotateCw className="mr-1.5 h-3.5 w-3.5" /> Reset
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => save(r.designation)}
                      disabled={!dirty(r.designation) || savingFor === r.designation}
                      data-testid={`pr-save-${r.designation}`}
                    >
                      <Save className="mr-1.5 h-3.5 w-3.5" />
                      {savingFor === r.designation ? "Saving\u2026" : "Save"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
